import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Field, Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  caregiverAdvancedFilterSections,
  getMultiEnumValues,
} from '../../util/caregiverSearch';
import { Form } from '../../components';

import css from './CaregiverSearchControls.module.css';

const labelIdForCategory = value => `CaregiverSearch.category.${value}`;
const labelIdForService = value => `CaregiverSearch.service.${value}`;
const labelIdForSection = value => `CaregiverSearch.section.${value}`;
const labelIdForField = value => `CaregiverSearch.field.${value}`;
const labelIdForOption = value => `CaregiverSearch.option.${value}`;
const headingIdForCategory = value => `CaregiverSearch.heading.${value}`;
const subheadingIdForCategory = value => `CaregiverSearch.subheading.${value}`;

const SearchPanel = props => {
  const intl = useIntl();
  const { queryParams, onSubmitMainSearch } = props;
  const keywords = (queryParams.keywords || '').trim();

  const initialValues = useMemo(
    () => ({
      keywords,
    }),
    [keywords]
  );

  return (
    <section className={css.searchPanel}>
      <div className={css.searchPanelHeader}>
        <div>
          <h2 className={css.searchPanelTitle}>
            <FormattedMessage id="CaregiverSearch.searchPanelTitle" />
          </h2>
          <p className={css.searchPanelText}>
            <FormattedMessage id="CaregiverSearch.searchPanelText" />
          </p>
        </div>
      </div>

      <FinalForm
        key={keywords}
        initialValues={initialValues}
        onSubmit={values => onSubmitMainSearch(values)}
        render={({ handleSubmit }) => (
          <Form className={css.searchForm} onSubmit={handleSubmit}>
            <label className={css.searchField}>
              <span className={css.searchFieldLabel}>
                <FormattedMessage id="CaregiverSearch.keywordLabel" />
              </span>
              <Field
                name="keywords"
                render={({ input }) => (
                  <input
                    {...input}
                    type="text"
                    className={css.searchTextInput}
                    placeholder={intl.formatMessage({
                      id: 'CaregiverSearch.keywordPlaceholder',
                    })}
                    autoComplete="off"
                  />
                )}
              />
            </label>

            <div className={classNames(css.searchField, css.searchActionField)}>
              <span className={css.searchFieldLabel} aria-hidden="true">
                &nbsp;
              </span>
              <button type="submit" className={css.searchButton}>
                <FormattedMessage id="CaregiverSearch.searchButton" />
              </button>
            </div>
          </Form>
        )}
      />
    </section>
  );
};

const AdvancedFilterSections = props => {
  const intl = useIntl();
  const { queryParams, fieldConfigs, onToggleMultiEnumFilter, onSetEnumFilter } = props;
  const [openSections, setOpenSections] = useState({
    experience: true,
    certifications: true,
    transportation: true,
    languages: true,
  });

  const toggleSection = sectionId => {
    setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  return caregiverAdvancedFilterSections.map(section => {
    const isOpen = !!openSections[section.id];
    return (
      <section key={section.id} className={css.accordionSection}>
        <button
          type="button"
          className={css.accordionButton}
          onClick={() => toggleSection(section.id)}
          aria-expanded={isOpen}
        >
          <span>
            {intl.formatMessage({
              id: labelIdForSection(section.id),
              defaultMessage: section.label,
            })}
          </span>
          <span className={classNames(css.accordionIcon, { [css.accordionIconOpen]: isOpen })}>
            +
          </span>
        </button>

        {isOpen ? (
          <div className={css.accordionBody}>
            {section.fields.map(field => {
              const fieldConfig = fieldConfigs[field.key];
              const options = fieldConfig?.enumOptions?.map(option => ({
                label: option.label,
                value: option.option,
              }));

              if (!options || options.length === 0) {
                return null;
              }

              if (field.type === 'enum') {
                const selectedValue = queryParams[`pub_${field.key}`] || null;
                return (
                  <div key={field.key} className={css.filterOptionGroup}>
                    {options.map(option => {
                      const isSelected = selectedValue === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={classNames(css.optionButton, {
                            [css.optionButtonSelected]: isSelected,
                          })}
                          onClick={() =>
                            onSetEnumFilter(field.key, isSelected ? null : option.value)
                          }
                        >
                          {intl.formatMessage({
                            id: labelIdForOption(option.value),
                            defaultMessage: option.label,
                          })}
                        </button>
                      );
                    })}
                  </div>
                );
              }

              const selectedValues = getMultiEnumValues(queryParams[`pub_${field.key}`]);

              return (
                <div key={field.key} className={css.filterOptionGroup}>
                  {options.map(option => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={classNames(css.optionButton, {
                          [css.optionButtonSelected]: isSelected,
                        })}
                        onClick={() => onToggleMultiEnumFilter(field.key, option.value)}
                      >
                        {intl.formatMessage({
                          id: labelIdForOption(option.value),
                          defaultMessage: option.label,
                        })}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  });
};

/**
 * Search control shell for the caregiver search redesign.
 *
 * @param {Object} props Component props.
 * @returns {JSX.Element}
 */
const CaregiverSearchControls = props => {
  const intl = useIntl();
  const {
    categories,
    servicesFilterComponent,
    selectedCategory,
    selectedFiltersCount,
    isAdvancedFiltersOpen,
    onSelectCategory,
    onToggleAdvancedFilters,
    onCloseAdvancedFilters,
    onToggleMultiEnumFilter,
    onSetEnumFilter,
    onResetAll,
    dateFilterComponent,
    sortByComponent,
    totalItems,
    searchInProgress,
    fieldConfigs,
    onSubmitMainSearch,
  } = props;

  const selectedCategoryConfig = categories.find(category => category.value === selectedCategory);
  const headingMessage = selectedCategoryConfig
    ? intl.formatMessage({
        id: headingIdForCategory(selectedCategoryConfig.value),
        defaultMessage: intl.formatMessage({ id: 'CaregiverSearch.heading' }),
      })
    : intl.formatMessage({ id: 'CaregiverSearch.heading' });
  const subheadingMessage = selectedCategoryConfig
    ? intl.formatMessage({
        id: subheadingIdForCategory(selectedCategoryConfig.value),
        defaultMessage: intl.formatMessage({ id: 'CaregiverSearch.subheading' }),
      })
    : intl.formatMessage({ id: 'CaregiverSearch.subheading' });

  const resultLabel = searchInProgress ? (
    <FormattedMessage id="CaregiverSearch.results.loading" />
  ) : (
    <FormattedMessage id="CaregiverSearch.results.count" values={{ count: totalItems }} />
  );

  return (
    <div className={css.root}>
      <div className={css.headingRow}>
        <div className={css.headingContent}>
          <p className={css.eyebrow}>
            <FormattedMessage id="CaregiverSearch.eyebrow" />
          </p>
          <h1 className={css.heading}>{headingMessage}</h1>
          <p className={css.subheading}>{subheadingMessage}</p>
        </div>
        <p className={css.resultsSummary}>{resultLabel}</p>
      </div>

      <div className={css.categoryShell}>
        <div
          className={css.categoryRow}
          aria-label={intl.formatMessage({ id: 'CaregiverSearch.categoriesAria' })}
        >
          {categories.map(category => {
            const isSelected = selectedCategory === category.value;
            return (
              <button
                key={category.value}
                type="button"
                className={classNames(css.categoryButton, {
                  [css.categoryButtonSelected]: isSelected,
                })}
                onClick={() => onSelectCategory(isSelected ? null : category.value)}
              >
                {intl.formatMessage({
                  id: labelIdForCategory(category.value),
                  defaultMessage: category.label,
                })}
              </button>
            );
          })}
        </div>
      </div>

      <SearchPanel
        queryParams={props.queryParams}
        onSubmitMainSearch={onSubmitMainSearch}
      />

      <div className={css.filterBarShell}>
        <div className={css.filterBar}>
          <label className={css.filterControl}>
            <span className={css.filterLabel}>
              <FormattedMessage id="CaregiverSearch.serviceLabel" />
            </span>
            <div className={css.embeddedControl}>{servicesFilterComponent}</div>
          </label>

          <div className={classNames(css.filterControl, css.dateControl)}>
            <span className={css.filterLabel}>
              <FormattedMessage id="CaregiverSearch.dateLabel" />
            </span>
            <div className={css.embeddedControl}>{dateFilterComponent}</div>
          </div>

          <div className={classNames(css.filterControl, css.filterButtonControl)}>
            <span className={css.filterLabel}>
              <FormattedMessage id="CaregiverSearch.filtersLabel" />
            </span>
            <button
              type="button"
              className={classNames(css.filtersButton, {
                [css.filtersButtonSelected]: selectedFiltersCount > 0,
              })}
              onClick={onToggleAdvancedFilters}
              aria-expanded={isAdvancedFiltersOpen}
            >
              <FormattedMessage
                id={
                  selectedFiltersCount > 0
                    ? 'CaregiverSearch.filtersButtonSelected'
                    : 'CaregiverSearch.filtersButton'
                }
                values={{ count: selectedFiltersCount }}
              />
            </button>
          </div>

          <div className={classNames(css.filterControl, css.sortControl)}>
            <span className={css.filterLabel}>
              <FormattedMessage id="CaregiverSearch.sortLabel" />
            </span>
            <div className={css.embeddedControl}>{sortByComponent}</div>
          </div>
        </div>
      </div>

      {isAdvancedFiltersOpen ? (
        <>
          <button
            type="button"
            className={css.mobileBackdrop}
            onClick={onCloseAdvancedFilters}
            aria-label={intl.formatMessage({ id: 'CaregiverSearch.closeFilters' })}
          />
          <div className={css.advancedPanel}>
            <div className={css.advancedPanelHeader}>
              <div>
                <h2 className={css.advancedPanelTitle}>
                  <FormattedMessage id="CaregiverSearch.advancedTitle" />
                </h2>
                <p className={css.advancedPanelHint}>
                  <FormattedMessage id="CaregiverSearch.advancedHint" />
                </p>
              </div>
              <div className={css.advancedPanelActions}>
                <button type="button" className={css.textButton} onClick={onResetAll}>
                  <FormattedMessage id="CaregiverSearch.resetAll" />
                </button>
                <button type="button" className={css.closeButton} onClick={onCloseAdvancedFilters}>
                  <FormattedMessage id="CaregiverSearch.done" />
                </button>
              </div>
            </div>

            <AdvancedFilterSections
              queryParams={props.queryParams}
              fieldConfigs={fieldConfigs}
              onToggleMultiEnumFilter={onToggleMultiEnumFilter}
              onSetEnumFilter={onSetEnumFilter}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CaregiverSearchControls;
